import { Store } from "game/store/gameStore";
import { Mission, MissionActionData, ActionConfig, Action, RewardType, AdditionalMissionConfigField } from "./types";
import { ConditionParser } from './ConditionParser';
import { GameUI } from "game/gameUI/GameUI";
import { GameEvent } from "game/events/gameEvent";
import { EventName } from "game/events/eventsName";
import { getRandomIntInclusive } from "game/utils/random";
import { getRandomUniqueMissionActions } from "./utils";
import { uiStore } from "ui/store";
import _ from "lodash";
import { GAEvent, GAEventNames } from "game/analytics/events";
import storiesConfig from 'game/gameUI/UiHandlers/StoriesHandler/__data__/stories_config.json';

export class QuestManager {
    private dailyMission?: Mission;
    private weeklyMission?: Mission;
    private missionConfig?: Pick<Mission, 'type' | 'rewards'> & AdditionalMissionConfigField & { testPeriodSec: number };
    private missionActionsConfig?: Array<ActionConfig>;

    private parser: ConditionParser;

    private static instance: QuestManager;

    constructor(private gameUI: GameUI) {
        GameEvent.add(EventName.GAME_READY, this.init.bind(this));
        GameEvent.add(EventName.MISSION_ACTION, this.handleMissionAction.bind(this));
        GameEvent.add(EventName.MISSION_CALIM_REWARDS, this.handleClaimRewards.bind(this));
        GameEvent.add(EventName.NEW_MISSIONS_REQUEST, this.handleNewMissionsRequest.bind(this));
        GameEvent.add(EventName.END_OF_MISSION_HANDLER, this.HandleEndOfMissionInRuntime.bind(this));
        document.addEventListener('missions-cheat-complete-all-missions', async () => {
            if (!this.dailyMission) {
                return;
            }
            this.dailyMission = {
                ...this.dailyMission,
                actions: this.dailyMission?.actions.map(a => ({
                    ...a,
                    passed: true
                })) || []
            };

            this.handleUpdateTooltip(true);
            
            await this.updateMissionState();

            this.updateMissionsUIState();
        });
        this.parser = new ConditionParser();
        QuestManager.instance = this;

        setInterval(() => GameEvent.emit(EventName.END_OF_MISSION_HANDLER), 1000);
    }

    private async init() {
        const missions = await Store.getMissionState();

        const targetMissions = missions[Store.name] || {};

        this.dailyMission = targetMissions?.dailyMission ? { ...targetMissions.dailyMission } : undefined;

        if (!this.dailyMission) {
            this.dailyMission = await this.getNewMission("daily");

            Store.updateMissionState({
                ...missions,
                [Store.name]: {
                    ...targetMissions,
                    dailyMission: this.dailyMission ? { ...this.dailyMission } : undefined
                }
            });
        }

        if (this.dailyMission) {
            const allCompleted = this.dailyMission.actions.every(a => a.progress >= a.targetProgress);
            const startOfToday = new Date();
            startOfToday.setHours(0, 0, 0, 0);
            const dayPassed = this.dailyMission.type === "daily" && this.dailyMission.startMs < startOfToday.getTime();

            if ((this.dailyMission.rewardsClaimed && allCompleted && dayPassed) || 
                (!this.dailyMission.rewardsClaimed && !allCompleted && dayPassed)
            ) {
                this.dailyMission = await this.getNewMission("daily");
                Store.updateMissionState({
                    ...missions,
                    [Store.name]: {
                        ...targetMissions,
                        dailyMission: this.dailyMission ? { ...this.dailyMission } : undefined
                    }
                });
            }
        }

        this.updateMissionsUIState();
    }

    private async HandleEndOfMissionInRuntime() {
        if (!this.dailyMission) {
            return;
        }

        const allCompleted = this.dailyMission.actions.every(a => a.passed);

        if (allCompleted && !this.dailyMission.rewardsClaimed) {
            return;
        }

        if (Date.now() > this.dailyMission.endMs) {
            this.dailyMission = await this.getNewMission("daily");
            await this.updateMissionState();
            this.updateMissionsUIState();
        }
    }

    public static getCurrentMission(type: 'daily' | 'weekly') {
        return type === 'daily' ? this.instance.dailyMission : this.instance.weeklyMission;
    }

    private async handleClaimRewards(type: 'daily' | 'weekly') {
        const mission = type === 'daily' ? this.dailyMission : this.weeklyMission;

        if (!mission) {
            return;
        }

        if (mission.rewardsClaimed) {
            return;
        }

        const rewards = mission.rewards;

        if (!rewards?.length) {
            return;
        }

        mission?.rewards.forEach((reward) => {
            const amount = mission.isAdditionalMission ? reward.adAmount : reward.amount;
            switch (reward.type) {
                case RewardType.HINTS:
                    Store.setHintCounter(Store.hintCounter + amount);
                    break;
                case RewardType.MAGIC_WAND:
                    Store.setWandCounter(Store.wandCounter + amount);
                    break;
            }
        });

        mission.rewardsClaimed = true;
        
        const startOfDay = new Date();
        startOfDay.setHours(0, 0, 0, 0);

        const startOfWeek = new Date();
        startOfWeek.setHours(0, 0, 0, 0);
        startOfWeek.setDate(startOfWeek.getDate() - startOfWeek.getDay());

        if (Date.now() > mission.endMs) {
            if (type === 'daily') {
                this.dailyMission = await this.getNewMission("daily");
            } else {
                this.weeklyMission = await this.getNewMission("weekly");
            }
        }

        await this.updateMissionState();
        this.updateMissionsUIState();
    }

    handleUpdateTooltip(isNewMissionShowOnMounted: boolean) {
        this.gameUI.updateUI({
            missions: {
                ..._.cloneDeep(uiStore.getState().gameUI.missions),
                isNewMissionCompleted: !isNewMissionShowOnMounted,
                isNewMissionCompletedOnMounted: isNewMissionShowOnMounted
            }
        });

        setTimeout(() => {
            this.gameUI.updateUI({
                missions: {
                    ..._.cloneDeep(uiStore.getState().gameUI.missions),
                    isNewMissionCompleted: false,
                }
            });
        }, 10000);
    }

    private async handleMissionAction(actionData: MissionActionData) {
        if (this.dailyMission) {

            let newPassedActions: Action[] = [];

            const prevPassedActions = this.dailyMission.actions.filter(a => a.passed).length;

            this.dailyMission = {
                ..._.cloneDeep(this.dailyMission),
                actions: _.cloneDeep(this.dailyMission.actions).map((action

                ) => {
                    if (action.type === actionData.type && !action.passed && (action.progress < action.targetProgress || action.targetProgress === 0)) {
                        const expression = action.condition.split('<target>').join(String(action.targetProgress));
                        
                        if (action.targetProgress === 0 && !this.parser.evaluate(actionData.payload, expression)) {
                            return action;
                        }
                        
                        let prevPassed = action.passed;

                        let newProgress = action.progress;
                        let newPassed = false;
                        if (action.increaseOnlyWhenCondition) {
                            if (this.parser.evaluate(actionData.payload, expression)) {
                                newProgress = action.progress + 1;
                                newPassed = newProgress >= action.targetProgress;
                            }
                        } else {
                            newProgress = actionData.currentProgress;
                            newPassed = this.parser.evaluate(actionData.payload, expression) || newProgress >= action.targetProgress;
                        }

                        if (newPassed !== prevPassed) {
                            newPassedActions.push(action);
                        }
                        
                        return {
                            ...action,
                            progress: newProgress,
                            passed: newPassed
                        };
                    }
                    return action;
                })
            };

            if (prevPassedActions < this.dailyMission.actions.filter(a => a.passed).length && !this.dailyMission.actions.find(a => !a.passed)) {
                const numberOfDailyMissions = localStorage.getItem('number-of-daily-missions');
                GAEvent(GAEventNames.MISSION_ALL_COMPLETED, {
                    number: numberOfDailyMissions ? Number(numberOfDailyMissions) : 1,
                });
            }

            if (newPassedActions.length) {
                newPassedActions.forEach(a => {
                    GAEvent(GAEventNames.MISSION_COMPLETED, {
                        type: a.tag,
                    });
                });

                setTimeout(() => {
                    this.handleUpdateTooltip(true);
                }, 1500);
            }

            await this.updateMissionState();

            this.updateMissionsUIState();
        }
    }

    private async updateMissionState() {
        const missions = await Store.getMissionState();

        Store.updateMissionState({
            ...missions,
            [Store.name]: {
                dailyMission: this.dailyMission ? { ...this.dailyMission } : undefined
            }
        });
    }

    private updateMissionsUIState() {
        this.gameUI.updateUI({
            missions: {
                ..._.cloneDeep(uiStore.getState().gameUI.missions),
                dailyMission: this.dailyMission ? { ..._.cloneDeep(this.dailyMission) } : undefined
            }
        });
    }

    private async handleNewMissionsRequest() {
        GAEvent(GAEventNames.MISSION_UNLOCK_BUTTON);

        GameEvent.emit(EventName.SHOW_ADS, { place: 'new-missions-request' });
    }
    
    public static async setupNewMission() {
        const generatedMission = await this.instance.getNewMission("daily", true);

        if (!generatedMission) {
            return;
        }

        this.instance.dailyMission = generatedMission;
        await this.instance.updateMissionState();
        this.instance.updateMissionsUIState();
    }

    private async getNewMission(type: "daily" | "weekly", adReason: boolean = false): Promise<Mission | undefined> {

        Store.clearContext();

        const startOfDay = new Date();
        startOfDay.setHours(0, 0, 0, 0);

        const startOfWeek = new Date();
        startOfWeek.setHours(0, 0, 0, 0);
        startOfWeek.setDate(startOfWeek.getDate() - startOfWeek.getDay());

        try {
            const fetchMissionConfig = () => import(`../missions/${Store.name.toLowerCase()}_mission.json`);
            const missionConfigResponse = await fetchMissionConfig();
            this.missionConfig = missionConfigResponse.default;

            const fetchMissionActionsConfig = () => import(`../missions/actions/${Store.name.toLowerCase()}_actions.json`);
            const missionActionsConfigResponse = await fetchMissionActionsConfig();
            this.missionActionsConfig = missionActionsConfigResponse.default;
        } catch {
            console.log('Mission config was not detected!');
        }

        if (!this.missionConfig || !this.missionActionsConfig) {
            return undefined;
        }

        const startMs = this.missionConfig?.testPeriodSec ? Date.now() : (type === "daily" ? startOfDay.getTime() : startOfWeek.getTime());

        const dailyPeriodMs = this.missionConfig?.testPeriodSec ? this.missionConfig.testPeriodSec * 1000 : 24 * 60 * 60 * 1000;
        const endMs = type === "daily" ? startMs + dailyPeriodMs : startOfWeek.getTime() + 7 * 24 * 60 * 60 * 1000;

        let targetActionConfigs: ActionConfig[] = [];

        const chanceByTag = adReason ? this.missionConfig?.chanceByTagSecondary : this.missionConfig?.chanceByTag;
        if (chanceByTag) {
            const allActions = this.missionActionsConfig || [];

            Object.keys(chanceByTag).forEach((tag) => {
                const chance = chanceByTag[tag].chance;
                const count = chanceByTag[tag].count;

                if (getRandomIntInclusive(0, 100) <= chance) {
                    targetActionConfigs.push(...getRandomUniqueMissionActions(allActions.filter((action) => action.tag === tag), count));
                }
            });
        } else {
            targetActionConfigs = this.missionActionsConfig || [];
        }

        if (targetActionConfigs.length === 0) {
            return undefined;
        }


        const numberOfDailyMissions = localStorage.getItem('number-of-daily-missions');

        if (!adReason || !numberOfDailyMissions) {
            if (!numberOfDailyMissions) {
                localStorage.setItem('number-of-daily-missions', '1');
            }
        }

        if (adReason) {
            const currentNumber = localStorage.getItem('number-of-daily-missions');
            localStorage.setItem('number-of-daily-missions', String(Number(currentNumber) + 1));
        }

        const actions: Action[] = targetActionConfigs.flatMap((action) => {
            let targetProgress = getRandomIntInclusive(action.target.min, action.target.max);

            if (action.target.step) {
                const steps = [];
                let current = action.target.min;

                while (current < action.target.max) {
                    steps.push(current);
                    current += action.target.step;
                }

                targetProgress = steps[getRandomIntInclusive(0, steps.length - 1)];
            }

            if (action.type === 'STORIES_UNLOCK_IMAGES') {
                const lastStoryProgress = parseInt(localStorage.getItem(`stories_story_${storiesConfig.length}_progress`) || '0');
                
                const difference = storiesConfig[storiesConfig.length - 1].totalImages - lastStoryProgress;

                targetProgress = Math.min(targetProgress, difference);

                if (targetProgress === 0) {
                    return [];
                }
            }

            return [{
                type: action.type,
                condition: action.condition,
                progress: 0,
                passed: false,
                label: action.labelKey,
                increaseOnlyWhenCondition: !!action.increaseOnlyWhenCondition,
                button: action.actionButton ? {
                    text: action.actionButton.textKey,
                    buttonActionType: action.actionButton.buttonActionType,
                    payload: action.actionButton.payload,
                    dataDraw: action.actionButton.dataDraw,
                    dataSuit: action.actionButton.dataSuit
                } : undefined,
                tag: action.tag,
                targetProgress
            }];
        });

        return {
            testPeriodSec: this.missionConfig?.testPeriodSec,
            isAdditionalMission: adReason,
            type: this.missionConfig.type,
            startMs,
            rewardsClaimed: false,
            rewards: this.missionConfig.rewards,
            actions,
            endMs
        }
    }
}